import React, { useState, useEffect, useRef } from 'react';
import { CinematicBackground, VanGoghCanvas, AlphaThetaSpiral } from './CinematicBackground';
import { SparkleIcon, ArrowIcon } from './BespokeIcons';

/**
 * ChapterTransition - Theatrical chapter transitions for Vision Quest
 * 
 * Creates movie-like chapter introductions with:
 * - Fade to black with subsonic rumble before transitions
 * - Auto-advance with skip option (mixed transitions)
 * - Cinematic typography
 * - Immersive backgrounds (Van Gogh, spiral, video)
 */

// Video URLs for chapters
const CHAPTER_VIDEOS = {
  video1: 'https://customer-assets.emergentagent.com/job_belonging-elchme/artifacts/b7wii3ny_gemini_generated_video_66D4CE13.mov',
  video2: 'https://customer-assets.emergentagent.com/job_belonging-elchme/artifacts/we1sfr1g_gemini_generated_video_429E56B6.mov'
};

// Chapter configurations
const CHAPTERS = {
  belonging: {
    number: 1,
    title: 'Belonging',
    subtitle: 'The Journey Inward',
    description: 'Discover the golden thread that connects us all',
    palette: 'starryNight',
    backgroundMode: 'vangogh',
    videoUrl: CHAPTER_VIDEOS.video1,
    duration: 8000, // 8 seconds auto-advance
  },
  vision: {
    number: 2,
    title: 'Vision',
    subtitle: 'The Quest Begins',
    description: 'Define your core values, purpose, and mission',
    palette: 'sunflowers',
    backgroundMode: 'video',
    videoUrl: CHAPTER_VIDEOS.video2,
    duration: 8000,
  },
  creation: {
    number: null,
    title: 'The Creation',
    subtitle: 'Your Story Emerges',
    description: 'From vision to narrative, your journey becomes legend',
    palette: 'almond',
    backgroundMode: 'spiral',
    videoUrl: null,
    duration: 6000,
  }
};

// Subsonic rumble audio generator (Web Audio API)
// IMPROVED: Added proper gain ramping to prevent audio clicking/popping
const useSubsonicRumble = () => {
  const audioContextRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const gainNodeRef = useRef(null);
  const isActiveRef = useRef(false);
  
  const startRumble = () => {
    // Prevent multiple starts
    if (isActiveRef.current) return;
    
    try {
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      
      audioContextRef.current = new AudioContext();
      const ctx = audioContextRef.current;
      isActiveRef.current = true;
      
      // Create gain node for volume control with smooth ramping
      gainNodeRef.current = ctx.createGain();
      gainNodeRef.current.gain.setValueAtTime(0.001, ctx.currentTime); // Start from near-zero (not 0 to avoid click)
      gainNodeRef.current.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 1.5); // Slower, smoother fade-in
      gainNodeRef.current.connect(ctx.destination);
      
      // Create fewer oscillators for cleaner sound (less interference)
      const frequencies = [22, 28]; // Just two low frequencies for cleaner rumble
      
      frequencies.forEach(freq => {
        const oscillator = ctx.createOscillator();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
        
        // Gentler modulation to reduce artifacts
        const modulator = ctx.createOscillator();
        modulator.frequency.setValueAtTime(0.2, ctx.currentTime); // Slower modulation
        const modulatorGain = ctx.createGain();
        modulatorGain.gain.setValueAtTime(1, ctx.currentTime); // Less modulation depth
        modulator.connect(modulatorGain);
        modulatorGain.connect(oscillator.frequency);
        modulator.start();
        
        oscillator.connect(gainNodeRef.current);
        oscillator.start();
        
        oscillatorsRef.current.push(oscillator, modulator);
      });
      
    } catch (e) {
      console.log('Audio not supported:', e);
      isActiveRef.current = false;
    }
  };
  
  const stopRumble = () => {
    if (!isActiveRef.current) return;
    
    try {
      if (gainNodeRef.current && audioContextRef.current) {
        const ctx = audioContextRef.current;
        const currentTime = ctx.currentTime;
        
        // Smooth exponential fade out to prevent clicks
        gainNodeRef.current.gain.setValueAtTime(gainNodeRef.current.gain.value || 0.12, currentTime);
        gainNodeRef.current.gain.exponentialRampToValueAtTime(0.001, currentTime + 1.5);
        
        // Wait for fade out before stopping oscillators
        setTimeout(() => {
          oscillatorsRef.current.forEach(osc => {
            try { osc.stop(); } catch(e) { /* already stopped */ }
          });
          oscillatorsRef.current = [];
          
          if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
          }
          isActiveRef.current = false;
        }, 1600);
      } else {
        isActiveRef.current = false;
      }
    } catch (e) {
      console.log('Error stopping audio:', e);
      isActiveRef.current = false;
    }
  };
  
  return { startRumble, stopRumble };
};

// Cinematic text reveal animation
const CinematicText = ({ children, delay = 0, className = '' }) => {
  const [revealed, setRevealed] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setRevealed(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  
  return (
    <div 
      className={`
        block w-full transition-all duration-1500 ease-out
        ${revealed ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-8 blur-sm'}
        ${className}
      `}
    >
      {children}
    </div>
  );
};

// Fade to Black component - IMPROVED: More reliable timing and callback handling
const FadeToBlack = ({ active, onComplete, duration = 2000 }) => {
  const { startRumble, stopRumble } = useSubsonicRumble();
  const [phase, setPhase] = useState('idle'); // idle, fading-in, holding, fading-out
  const completedRef = useRef(false);
  
  useEffect(() => {
    if (!active) {
      setPhase('idle');
      completedRef.current = false;
      return;
    }
    
    // Reset completed flag when activated
    completedRef.current = false;
    
    // Start fade in
    setPhase('fading-in');
    startRumble();
    
    // Hold at full black
    const holdTimer = setTimeout(() => {
      setPhase('holding');
    }, 800);
    
    // Start fade out
    const fadeOutTimer = setTimeout(() => {
      setPhase('fading-out');
      stopRumble();
    }, duration - 800);
    
    // Complete - ensure callback fires only once
    const completeTimer = setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        setPhase('idle');
        // Use requestAnimationFrame to ensure smooth callback timing
        requestAnimationFrame(() => {
          onComplete?.();
        });
      }
    }, duration + 100); // Small buffer to ensure fade completes
    
    return () => {
      clearTimeout(holdTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(completeTimer);
      stopRumble();
    };
  }, [active, duration]); // Removed onComplete from deps to prevent re-triggers
  
  if (phase === 'idle') return null;
  
  return (
    <div 
      className={`
        fixed inset-0 z-[100] bg-black pointer-events-none
        transition-opacity duration-800 ease-in-out
        ${phase === 'fading-in' || phase === 'holding' ? 'opacity-100' : 'opacity-0'}
      `}
      data-testid="fade-to-black"
    />
  );
};

// Main chapter transition component
export const ChapterTransition = ({
  chapter = 'belonging',
  onComplete,
  autoAdvance = true,
  showSkip = true,
  showFadeIn = true // Show fade from black at start
}) => {
  const [phase, setPhase] = useState(showFadeIn ? 'black' : 'fade-in'); // black, fade-in, content, fade-out
  const [progress, setProgress] = useState(0);
  const [showFadeToBlack, setShowFadeToBlack] = useState(false);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const { startRumble, stopRumble } = useSubsonicRumble();
  const config = CHAPTERS[chapter] || CHAPTERS.belonging;
  
  // Initial fade from black
  useEffect(() => {
    if (showFadeIn) {
      startRumble();
      const fadeTimer = setTimeout(() => {
        stopRumble();
        setPhase('fade-in');
      }, 1500);
      
      return () => {
        clearTimeout(fadeTimer);
        stopRumble();
      };
    }
  }, [showFadeIn, startRumble, stopRumble]);
  
  // Handle auto-advance timer
  useEffect(() => {
    if (!autoAdvance || phase === 'black') return;
    
    // Start progress bar after fade-in
    if (phase === 'fade-in') {
      const contentTimer = setTimeout(() => setPhase('content'), 500);
      return () => clearTimeout(contentTimer);
    }
    
    if (phase !== 'content') return;
    
    // Start progress bar
    progressRef.current = setInterval(() => {
      setProgress(prev => Math.min(prev + (100 / (config.duration / 100)), 100));
    }, 100);
    
    // Auto-advance after duration
    timerRef.current = setTimeout(() => {
      handleComplete();
    }, config.duration);
    
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [autoAdvance, config.duration, phase]);
  
  const handleComplete = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    
    // Trigger fade to black before completing
    setShowFadeToBlack(true);
  };
  
  const onFadeToBlackComplete = () => {
    setShowFadeToBlack(false);
    onComplete?.();
  };
  
  const handleSkip = () => {
    handleComplete();
  };
  
  // Determine background - random between options
  const getBackgroundMode = () => {
    if (config.backgroundMode === 'video' && config.videoUrl) {
      return Math.random() > 0.3 ? 'video' : 'vangogh';
    }
    return config.backgroundMode || 'vangogh';
  };
  
  const [bgMode] = useState(getBackgroundMode);
  
  return (
    <>
      {/* Fade to Black overlay */}
      <FadeToBlack 
        active={showFadeToBlack} 
        onComplete={onFadeToBlackComplete}
        duration={2500}
      />
      
      <div 
        className={`
          fixed inset-0 z-50 flex items-center justify-center overflow-hidden px-4 pb-28
          transition-opacity duration-1000
          ${phase === 'black' ? 'opacity-0' : 'opacity-100'}
        `}
        data-testid={`chapter-transition-${chapter}`}
      >
        {/* Cinematic Background */}
        <div className="absolute inset-0">
          {bgMode === 'video' && config.videoUrl ? (
            <>
              <video
                src={config.videoUrl}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ opacity: 0.5 }}
                autoPlay
                loop
                muted
                playsInline
              />
              <div className="absolute inset-0 bg-black/40" />
            </>
          ) : bgMode === 'spiral' ? (
            <AlphaThetaSpiral speed={0.15} colorShift={true} />
          ) : (
            <VanGoghCanvas palette={config.palette} speed={0.2} intensity={0.8} />
          )}
          
          {/* Vignette */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.7) 100%)'
            }}
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 text-center px-8 max-w-3xl">
          {/* Chapter number */}
          {config.number && (
            <CinematicText delay={500}>
              <p className="text-white/40 text-sm tracking-[0.4em] uppercase mb-6">
                Chapter {config.number}
              </p>
            </CinematicText>
          )}
          
          {/* Main title */}
          <CinematicText delay={1000}>
            <h1 
              className="text-6xl sm:text-7xl lg:text-8xl font-light tracking-wide mb-6"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                color: chapter === 'creation' ? '#FBBF24' : '#FFFFFF',
                textShadow: '0 0 60px rgba(255,255,255,0.3)'
              }}
            >
              {config.title}
            </h1>
          </CinematicText>
          
          {/* Subtitle */}
          <CinematicText delay={1800}>
            <p 
              className="text-xl sm:text-2xl text-amber-400/80 mb-4 tracking-wider"
              style={{ fontFamily: "'Figtree', sans-serif" }}
            >
              {config.subtitle}
            </p>
          </CinematicText>
          
          {/* Description */}
          <CinematicText delay={2500}>
            <p className="text-white/60 text-lg max-w-xl mx-auto leading-relaxed">
              {config.description}
            </p>
          </CinematicText>
          
          {/* Decorative element */}
          <CinematicText delay={3200}>
            <div className="mt-10 flex justify-center">
              <SparkleIcon size={32} color="#FBBF24" className="animate-pulse" />
            </div>
          </CinematicText>
        </div>
        
        {/* Bottom controls: progress + prominent Continue button (mobile-friendly) */}
        {phase === 'content' && (
          <div
            className="absolute inset-x-0 bottom-0 z-20 flex flex-col items-center gap-4 px-6 pt-6"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.75rem)' }}
          >
            {/* Progress bar (if auto-advance) */}
            {autoAdvance && (
              <div className="w-48 sm:w-64">
                <div className="h-0.5 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500/60 transition-all duration-100 ease-linear"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Continue button - large touch target, clearly visible */}
            {showSkip && (
              <button
                onClick={handleSkip}
                className="group inline-flex items-center justify-center gap-2 min-h-[48px] px-7 py-3 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 hover:text-amber-100 hover:border-amber-400/70 active:scale-[0.98] backdrop-blur-md transition-[background-color,border-color,color,transform] duration-200 text-sm font-medium tracking-wide shadow-lg shadow-black/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
                data-testid="skip-chapter-btn"
                aria-label="Continue to next step"
              >
                <span>Continue</span>
                <ArrowIcon size={16} color="currentColor" direction="right" />
              </button>
            )}
          </div>
        )}
        
        {/* Film grain overlay */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            animation: 'grain 0.5s steps(1) infinite'
          }}
        />
        
        <style>{`
          @keyframes grain {
            0%, 100% { transform: translate(0, 0); }
            10% { transform: translate(-1%, -1%); }
            20% { transform: translate(1%, 1%); }
            30% { transform: translate(-1%, 1%); }
            40% { transform: translate(1%, -1%); }
            50% { transform: translate(-1%, 0%); }
            60% { transform: translate(1%, 0%); }
            70% { transform: translate(0%, 1%); }
            80% { transform: translate(0%, -1%); }
            90% { transform: translate(1%, 1%); }
          }
        `}</style>
      </div>
    </>
  );
};

// Export helper for chapter video URLs
export const getChapterVideo = (chapter) => CHAPTERS[chapter]?.videoUrl;
export { CHAPTERS, CHAPTER_VIDEOS, FadeToBlack, useSubsonicRumble };
export default ChapterTransition;
